import { parseApiError } from "../shared/api-errors";
import { withIdempotencyKey } from "../shared/idempotency";
import { toSubmitPurposes } from "../api/submit-payload";
import { toLanguageCode } from "../api/language";
import type { TTSAudioUrlsResponse } from "../api/types";
import { ASSISTED_BASE_URL, ASSISTED_FALLBACK_MESSAGES } from "./constants";
import type {
  AssistedNoticeResponse,
  CreateOtpDetail,
  CreateOtpParams,
  CreateOtpResponse,
  FetchAssistedNoticeParams,
  FetchAssistedTTSAudioUrlsParams,
  SubmitAssistedConsentParams,
  VerifyOtpDetail,
  VerifyOtpParams,
  VerifyOtpResponse,
} from "./types";

const publicScope = (
  baseUrl: string | undefined,
  organisationUuid: string,
  workspaceUuid: string
): string =>
  `${baseUrl || ASSISTED_BASE_URL}/public/organisations/${organisationUuid}/workspaces/${workspaceUuid}`;

export const fetchAssistedNotice = async ({
  organisationUuid,
  workspaceUuid,
  noticeUuid,
  baseUrl,
  signal,
}: FetchAssistedNoticeParams): Promise<AssistedNoticeResponse> => {
  if (!organisationUuid || !workspaceUuid || !noticeUuid) {
    throw new Error("organisationUuid, workspaceUuid and noticeUuid are required");
  }

  const url = `${publicScope(baseUrl, organisationUuid, workspaceUuid)}/notices/get-notice/${noticeUuid}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, ASSISTED_FALLBACK_MESSAGES.notice);
  }

  return (await response.json()) as AssistedNoticeResponse;
};

export const createOtp = async ({
  organisationUuid,
  workspaceUuid,
  to,
  channel,
  baseUrl,
  signal,
}: CreateOtpParams): Promise<CreateOtpDetail> => {
  if (!organisationUuid || !workspaceUuid || !to) {
    throw new Error("organisationUuid, workspaceUuid and to are required");
  }

  const url = `${publicScope(baseUrl, organisationUuid, workspaceUuid)}/otp/create`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ to, channel }),
    signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, ASSISTED_FALLBACK_MESSAGES.createOtp);
  }

  const data = (await response.json()) as CreateOtpResponse;
  return data.detail;
};

export const verifyOtp = async ({
  organisationUuid,
  workspaceUuid,
  uuid,
  otp,
  purpose,
  baseUrl,
  signal,
}: VerifyOtpParams): Promise<VerifyOtpDetail> => {
  if (!organisationUuid || !workspaceUuid || !uuid || !otp) {
    throw new Error("organisationUuid, workspaceUuid, uuid and otp are required");
  }

  const url = `${publicScope(baseUrl, organisationUuid, workspaceUuid)}/otp/verify`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ uuid, otp, purpose }),
    signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, ASSISTED_FALLBACK_MESSAGES.verifyOtp);
  }

  const data = (await response.json()) as VerifyOtpResponse;
  return data.detail;
};

export const submitAssistedConsent = async ({
  accessToken,
  organisationUuid,
  workspaceUuid,
  baseUrl,
  ledgerBaseUrl,
  noticeUuid,
  purposes,
  language,
  signal,
}: SubmitAssistedConsentParams): Promise<Record<string, unknown> | null> => {
  if (!accessToken || !noticeUuid || !Array.isArray(purposes)) {
    throw new Error("accessToken, noticeUuid and purposes array are required");
  }

  const payload = {
    notice_uuid: noticeUuid,
    purposes: toSubmitPurposes(purposes),
    select_all_mandatory: false,
    source: "WEB",
    declined: false,
    language: toLanguageCode(language),
  };

  const url = `${publicScope(ledgerBaseUrl || baseUrl, organisationUuid, workspaceUuid)}/submit-consent`;
  const serialisedBody = JSON.stringify(payload);

  const response = await withIdempotencyKey(url, serialisedBody, (idempotencyHeaders) =>
    fetch(url, {
      method: "POST",
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        ...idempotencyHeaders,
      },
      body: serialisedBody,
      signal,
    })
  );

  if (!response.ok) {
    throw await parseApiError(response, ASSISTED_FALLBACK_MESSAGES.submit);
  }

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const fetchAssistedTTSAudioUrls = async ({
  organisationUuid,
  workspaceUuid,
  noticeUuid,
  language,
  baseUrl,
  signal,
}: FetchAssistedTTSAudioUrlsParams): Promise<TTSAudioUrlsResponse> => {
  if (!organisationUuid || !workspaceUuid || !noticeUuid || !language) {
    throw new Error("organisationUuid, workspaceUuid, noticeUuid and language are required");
  }

  const url = `${publicScope(baseUrl, organisationUuid, workspaceUuid)}/notices/${noticeUuid}/audio/${language}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, ASSISTED_FALLBACK_MESSAGES.audio);
  }

  return (await response.json()) as TTSAudioUrlsResponse;
};
